<template>
  <Dialog
    v-model:visible="displayModal"
    data-test-id="choose-items-dialog"
    :header="t(header)"
    :modal="true"
    :dismissable-mask="true"
    :style="{ width: '70vw' }"
    :breakpoints="{ '960px': '85vw', '640px': '95vw' }"
    @update:visible="handleClose"
  >
    <!-- Search bar -->
    <div class="search-container mb-3">
      <span class="p-input-icon-left w-full">
        <i class="fa-thin fa-magnifying-glass" />
        <InputText
          v-model="searchValue"
          data-test-id="search-item-input"
          :placeholder="t('templateBuilder.modals.typeFirstLetters')"
          class="w-full"
          @input="handleValueChange"
        />
      </span>
    </div>

    <!-- Labels -->
    <div class="flex labels-row mb-2">
      <div class="pane-label available-label">
        <span class="font-semibold">{{ t('templateBuilder.modals.availableItems') }}</span>
        <span class="count-badge">{{ availableList.length }}</span>
      </div>
      <div class="spacer" />
      <div class="pane-label chosen-label">
        <span class="font-semibold">{{ t('templateBuilder.modals.selectedItems') }}</span>
        <span class="count-badge selected">{{ selectedList.length }}</span>
      </div>
    </div>

    <!-- Main content -->
    <div class="flex pane-container">
      <!-- Available items -->
      <div class="pane available-pane">
        <div
          v-if="isLoading && availableList.length === 0"
          class="loading-state"
        >
          <i class="fa-solid fa-spinner fa-spin fa-2x" />
          <p>{{ t('templateBuilder.modals.loading') }}</p>
        </div>
        <div
          v-else-if="availableList.length === 0 && searchValue.length > 0"
          class="empty-state"
        >
          <i class="fa-thin fa-box-open fa-3x mb-2" />
          <p>{{ t('templateBuilder.modals.noItems.available') }}</p>
        </div>
        <div
          v-else-if="availableList.length === 0"
          class="empty-state"
        >
          <i class="fa-thin fa-magnifying-glass fa-3x mb-2" />
          <p>{{ t('templateBuilder.modals.searchHint') }}</p>
        </div>
        <div
          v-else
          ref="availableListRef"
          class="items-list"
          @scroll="handleAvailableScroll"
        >
          <div
            v-for="item in availableList"
            :key="item[fields.id]"
            class="list-item"
            @click="selectItem(item)"
          >
            <div class="item-info">
              <span class="item-id">#{{ item[fields.elementId] }}</span>
              <span class="item-name">{{ item[fields.label] }}</span>
            </div>
            <i class="fa-solid fa-plus item-action add" />
          </div>
          <div
            v-if="isLoadingMore"
            class="loading-more"
          >
            <i class="fa-solid fa-spinner fa-spin" />
            <span>{{ t('templateBuilder.modals.loadingMore') }}</span>
          </div>
          <div
            v-if="!hasMoreResults && availableList.length > 0"
            class="end-of-list"
          >
            <span>{{ t('templateBuilder.modals.endOfList') }}</span>
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="action-buttons">
        <div class="action-hint">
          <i class="fa-solid fa-arrow-right" />
        </div>
      </div>

      <!-- Selected items -->
      <div class="pane chosen-pane">
        <div
          v-if="selectedList.length === 0"
          class="empty-state"
        >
          <i class="fa-thin fa-clipboard-list fa-3x mb-2" />
          <p>{{ t('templateBuilder.modals.noItems.selected') }}</p>
        </div>
        <div
          v-else
          class="items-list"
        >
          <div
            v-for="item in selectedList"
            :key="item[fields.id]"
            class="list-item selected"
            @click="removeItem(item)"
          >
            <div class="item-info">
              <span class="item-id">#{{ item[fields.elementId] }}</span>
              <span class="item-name">{{ item[fields.label] }}</span>
            </div>
            <i class="fa-solid fa-minus item-action remove" />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-content-between align-items-center w-full">
        <span class="selection-summary">
          {{ t('templateBuilder.modals.itemsSelected', { count: selectedList.length }) }}
        </span>
        <div class="flex gap-2">
          <Button
            data-test-id="choose-items-cancel-button"
            :label="t('templateBuilder.modals.cancel')"
            class="p-button-secondary"
            @click="handleClose"
          />
          <Button
            data-test-id="choose-items-validate-button"
            :label="t('templateBuilder.modals.validate')"
            class="validate-btn"
            @click="handleChooseItems"
          >
            <i class="fa-solid fa-check mr-2" />
            {{ t('templateBuilder.modals.validate') }}
          </Button>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  computed, ComputedRef, defineComponent, onMounted, PropType, ref, Ref,
} from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { useI18n } from 'vue-i18n';
import { ListSettings } from '@/composables/GraphQL';
import { SpmTableFilter, StringMap } from '@/types';
import { showToastError } from '@/helpers';
import { OperatorType } from '@/types/generated-types/graphql';

const PAGE_SIZE = 50;

// Types supportant FULLTEXT search avec recherche par ID
const FULLTEXT_CONFIG: Record<string, string> = {
  ShopsProducts: 'id_product',
  ShopsProductsCategoriesList: 'id_category',
  ShopsManufacturers: 'id_manufacturer',
};

export default defineComponent({
  name: 'ChooseItemsModal',

  components: {
    Dialog,
    InputText,
    Button,
  },

  props: {
    displayDialog: {
      type: Boolean,
      required: true,
    },

    header: {
      type: String,
      required: true,
    },

    name: {
      type: String,
      required: true,
    },

    fields: {
      type: Object as PropType<StringMap>,
      required: true,
    },

    items: {
      type: String,
      required: false,
      default: '',
    },

    defaultFilters: {
      type: Array as PropType<SpmTableFilter[]>,
    },
  },

  emits: {
    'on-close-dialog': null,
    'on-choose-items': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const displayModal: ComputedRef<boolean> = computed(() => props.displayDialog);
    const searchValue: Ref<string> = ref('');
    const availableListRef = ref<HTMLElement | null>(null);

    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const hasMoreResults = ref(true);
    const currentOffset = ref(0);
    let searchTimer: ReturnType<typeof setTimeout> | null = null;
    let searchRequestId = 0;

    const settings: Ref<ListSettings> = ref({
      limit: PAGE_SIZE,
      offset: 0,
      order: [],
      filter: [],
    });

    const availableList: Ref<StringMap[]> = ref([]);
    const selectedList: Ref<StringMap[]> = ref([]);

    // AbortController pour annuler les requêtes en cours
    let currentAbortController: AbortController | null = null;

    const fetchDataWithSignal = async (fetchSettings: ListSettings, signal: AbortSignal): Promise<StringMap[]> => {
      const fname = `${props.name}List`;
      // Limiter aux champs essentiels pour optimiser la requête
      const fieldsStr = `${props.fields.id} ${props.fields.elementId} ${props.fields.label}`;

      const query = `query ($limit: Int!, $offset: Int!, $order: [OrderParameters], $filter: [FilterParameters]) {
        ${fname} (params: {limit: $limit, offset: $offset, order: $order, filter: $filter}) {
          items { ${fieldsStr} }
        }
      }`;

      try {
        const response = await fetch('/api/graphql', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query,
            variables: fetchSettings,
          }),
          signal,
        });

        const result = await response.json();
        return result.data?.[fname]?.items ?? [];
      } catch (error: any) {
        if (error.name === 'AbortError') {
          return [];
        }
        showToastError('GENERIC_ERROR');
        return [];
      }
    };

    const fetchData = async (): Promise<StringMap[]> => fetchDataWithSignal(settings.value, currentAbortController?.signal ?? new AbortController().signal);

    const filterOutSelected = (items: StringMap[]): StringMap[] => items.filter(
      (item) => !selectedList.value.some((selected) => selected[props.fields.id] === item[props.fields.id]),
    );

    const handleValueChange = async () => {
      if (searchTimer) clearTimeout(searchTimer);

      // Annuler la requête précédente
      if (currentAbortController) {
        currentAbortController.abort();
      }

      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      const searchTerm = searchValue.value.trim();
      if (!searchTerm) {
        availableList.value = [];
        isLoading.value = false;
        return;
      }

      searchTimer = setTimeout(async () => {
        if (currentRequestId !== searchRequestId) return;

        // Créer un nouveau AbortController pour cette recherche
        currentAbortController = new AbortController();
        const { signal } = currentAbortController;

        isLoading.value = true;
        currentOffset.value = 0;
        hasMoreResults.value = true;
        availableList.value = [];

        const isFulltextType = props.name in FULLTEXT_CONFIG;
        const isSingleWord = !searchTerm.includes(' ');

        settings.value.limit = PAGE_SIZE;
        settings.value.offset = 0;

        // Utiliser FULLTEXT (Against) pour products, categories et manufacturers
        // Le filtre de recherche DOIT être en premier pour optimiser la requête FULLTEXT
        const searchOperator = isFulltextType ? OperatorType.Against : OperatorType.Contains;
        settings.value.filter = [
          { field: props.fields.label, value: searchTerm, operator: searchOperator },
          ...(props.defaultFilters ?? []),
        ];

        let results: StringMap[] = [];

        // Si FULLTEXT type + single word : recherche par nom ET par ID en parallèle
        if (isFulltextType && isSingleWord) {
          const idField = FULLTEXT_CONFIG[props.name];

          // Requête par ID (filtre ID en premier)
          const idSettings: ListSettings = {
            limit: 1,
            offset: 0,
            order: [],
            filter: [
              { field: idField, value: searchTerm, operator: OperatorType.Equals },
              ...(props.defaultFilters ?? []),
            ],
          };

          const [nameResults, idResults] = await Promise.all([
            fetchDataWithSignal(settings.value, signal),
            fetchDataWithSignal(idSettings, signal),
          ]);

          if (currentRequestId !== searchRequestId) return;

          // Fusionner : ID en premier, puis résultats par nom (sans doublons)
          results = [...idResults];
          nameResults.forEach((item) => {
            if (!results.some((r) => r[props.fields.id] === item[props.fields.id])) {
              results.push(item);
            }
          });
        } else {
          results = await fetchDataWithSignal(settings.value, signal);
          if (currentRequestId !== searchRequestId) return;
        }

        availableList.value = filterOutSelected(results);
        hasMoreResults.value = results.length >= PAGE_SIZE;
        currentOffset.value = PAGE_SIZE;
        isLoading.value = false;
      }, 500);
    };

    const loadMore = async () => {
      if (isLoadingMore.value || !hasMoreResults.value || !currentAbortController) return;

      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      isLoadingMore.value = true;
      settings.value.offset = currentOffset.value;

      const results = await fetchDataWithSignal(settings.value, currentAbortController.signal);

      if (currentRequestId !== searchRequestId) {
        isLoadingMore.value = false;
        return;
      }

      if (results.length > 0) {
        const newItems = filterOutSelected(results);
        availableList.value = [...availableList.value, ...newItems];
        hasMoreResults.value = results.length === PAGE_SIZE;
        currentOffset.value += PAGE_SIZE;
      } else {
        hasMoreResults.value = false;
      }
      isLoadingMore.value = false;
    };

    const handleAvailableScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMore();
      }
    };

    const handleClose = () => {
      context.emit('on-close-dialog');
    };

    const selectItem = (element: StringMap) => {
      selectedList.value.unshift(element);
      availableList.value = availableList.value.filter((el: StringMap) => el[props.fields.id] !== element[props.fields.id]);
    };

    const removeItem = (element: StringMap) => {
      selectedList.value = selectedList.value.filter((el: StringMap) => el[props.fields.id] !== element[props.fields.id]);
    };

    const handleChooseItems = () => {
      context.emit('on-choose-items', selectedList.value);
      handleClose();
    };

    onMounted(async () => {
      if (props.items && props.items !== '') {
        settings.value.filter = [
          { field: props.fields.id, value: props.items, operator: OperatorType.In },
        ];
        settings.value.limit = 1000;
        selectedList.value = await fetchData();
        settings.value.limit = PAGE_SIZE;
      }
    });

    return {
      t,
      searchValue,
      availableList,
      selectedList,
      displayModal,
      availableListRef,
      selectItem,
      removeItem,
      handleClose,
      handleValueChange,
      handleChooseItems,
      handleAvailableScroll,
      isLoading,
      isLoadingMore,
      hasMoreResults,
    };
  },
});
</script>

<style scoped lang="scss">
@import '@/assets/scss/utilities/_variables.scss';

.search-container {
  .p-inputtext {
    padding-left: 2.5rem;
  }
}

.labels-row {
  .pane-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &.available-label {
      width: 45%;
    }

    &.chosen-label {
      width: 45%;
    }
  }

  .spacer {
    width: 10%;
  }

  .count-badge {
    background: $extra-light-grey;
    color: $tuna;
    padding: 0.15rem 0.5rem;
    border-radius: 1rem;
    font-size: 0.8rem;
    font-weight: 600;

    &.selected {
      background: lighten($brand-color-primary, 35%);
      color: darken($brand-color-primary, 20%);
    }
  }
}

.pane-container {
  height: 350px;
  gap: 0;
}

.pane {
  height: 100%;
  border: 1px solid $solitude;
  border-radius: 6px;
  overflow: hidden;
  background: $white;

  &.available-pane {
    width: 45%;
  }

  &.chosen-pane {
    width: 45%;
  }
}

.action-buttons {
  width: 10%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .action-hint {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $extra-light-grey;
    border-radius: 50%;
    color: $mid-grey;
  }
}

.items-list {
  height: 100%;
  overflow-y: auto;
  padding: 0.5rem;
}

.list-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.25rem;

  &:hover {
    background: $extra-light-grey;

    .item-action {
      opacity: 1;
    }
  }

  &.selected {
    background: lighten($brand-color-primary, 42%);

    &:hover {
      background: lighten($brand-color-primary, 38%);
    }
  }

  .item-info {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;

    .item-id {
      font-size: 0.75rem;
      color: $mid-grey;
      font-weight: 500;
    }

    .item-name {
      font-size: 0.875rem;
      color: $tuna;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .item-action {
    opacity: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &.add {
      background: $brand-color-primary;
      color: $white;
    }

    &.remove {
      background: #dc3545;
      color: $white;
    }
  }
}

.empty-state,
.loading-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: $mid-grey;
  text-align: center;
  padding: 1rem;

  i {
    color: $mid-grey;
    margin-bottom: 0.5rem;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
  }
}

.loading-more,
.end-of-list {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  color: $mid-grey;
  font-size: 0.85rem;
}

.selection-summary {
  color: $mid-grey;
  font-size: 0.9rem;
}

.validate-btn {
  background: $brand-color-primary;
  border-color: $brand-color-primary;

  &:hover {
    background: $successButtonHoverBg;
    border-color: $successButtonHoverBg;
  }
}
</style>
